import axios from "axios"
import AxiosConfig from "src/utilities/AxiosConfig"
import snackBar from "src/utilities/snackBar/SnackBar"
import PositiveResponseService from "./PositiveResponseService"
import PositiveResponseHashService from "./PositiveResponseHashService"

const { manageState } = PositiveResponseService
const { buildBTPRTable } = PositiveResponseHashService

// Shape returned by api/positiveResponse/addPositiveResponseRerun. Queued says whether a send is
// now pending; Outcome names which path the API took.
interface RerunResult {
    TicketPositiveResponseId: number
    Outcome: string
    Queued: boolean
}

// Why a re-run did not result in anything being queued, in operator-facing wording.
const skippedReasons: { [outcome: string]: string } = {
    AlreadySent: "already sent",
    NoActiveConfiguration: "no active sender configuration",
    ScanIncomplete: "the send queue was busy, try again"
}

// The endpoint used to return a bare `true`. Treat anything without a Queued flag as accepted so a
// frontend deploy ahead of the API deploy does not report false warnings.
const toRerunResult = (data: any, ticketPositiveResponseId: number): RerunResult => {
    if (data && typeof data.Queued === "boolean") {
        return {
            TicketPositiveResponseId: data.TicketPositiveResponseId || ticketPositiveResponseId,
            Outcome: data.Outcome || "",
            Queued: data.Queued
        }
    }

    return { TicketPositiveResponseId: ticketPositiveResponseId, Outcome: "Queued", Queued: true }
}

// Sends complete asynchronously well after the request returns, so the operator needs to be told
// what was accepted rather than being left to infer it from the list refreshing.
const reportRerunOutcomes = (results: RerunResult[]) => {
    if (results.length === 0) {
        return
    }

    const queued = results.filter(result => result.Queued)
    const skipped = results.filter(result => !result.Queued)

    if (skipped.length === 0) {
        snackBar({
            text: `${queued.length} positive response${queued.length === 1 ? "" : "s"} queued for resend. Sending completes within a few minutes.`,
            type: "success",
            timeout: 6000
        })
        return
    }

    const reasons = skipped
        .map(result => skippedReasons[result.Outcome] || result.Outcome)
        .filter((reason, index, all) => all.indexOf(reason) === index)
        .join("; ")

    snackBar({
        text: `${queued.length} queued, ${skipped.length} not sent (${reasons}).`,
        type: "warning",
        timeout: 8000
    })
}

const PositiveResponseCrudService = {
    loggedInUserId: -1,
    getBadTicketPositiveResponses: () => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/positiveResponse/getRerunnableTicketPositiveResponses`, AxiosConfig.configValue())
            .then(res => {
                const btprs = res.data
                buildBTPRTable(btprs)
                manageState([
                    { key: "badTicketPositiveResponses", value: btprs },
                    { key: "firstLoad", value: false },
                    { key: "busyId", value: -1 }
                ])
            })
            .catch(() => {
                snackBar({ text: "Error fetching positive responses", type: "error", timeout: 4000 })
                manageState([
                    { key: "badTicketPositiveResponses", value: [] },
                    { key: "firstLoad", value: false },
                    { key: "busyId", value: -1 }
                ])
            })
    },
    addPositiveResponseRerun: (ticketPositiveResponseIds: number[], results: RerunResult[] = []) => {
        if (ticketPositiveResponseIds.length === 0) {
            // Whole batch processed - summarise before the list refetch clears the rows.
            reportRerunOutcomes(results)
            PositiveResponseCrudService.getBadTicketPositiveResponses()
            return // Exit condition
        }

        const TicketPositiveResponseId = ticketPositiveResponseIds[0]
        const { loggedInUserId } = PositiveResponseCrudService

        manageState([
            { key: "busyId", value: TicketPositiveResponseId }
        ])

        axios.get(`${process.env.REACT_APP_API_URL}/api/positiveResponse/AddPositiveResponseRerun/${TicketPositiveResponseId}/${loggedInUserId}`, AxiosConfig.configValue())
            .then(res => {
                results.push(toRerunResult(res.data, TicketPositiveResponseId))
                // Recursively call the function for the remaining IDs
                PositiveResponseCrudService.addPositiveResponseRerun(ticketPositiveResponseIds.slice(1), results)
            })
            .catch(() => {
                // A failure aborts the rest of the batch, so report what did get queued before it -
                // otherwise a partial batch looks like a total failure.
                reportRerunOutcomes(results)
                snackBar({ text: "Error adding positive response", type: "error", timeout: 4000 })
                manageState([
                    { key: "busyId", value: -1 }
                ])
            })
    },
    ignorePositiveResponseErrors: (ticketPositiveResponseIds: number[]) => {
        if (ticketPositiveResponseIds.length === 0) {
            PositiveResponseCrudService.getBadTicketPositiveResponses()
            return // Exit condition
        }

        const TicketPositiveResponseId = ticketPositiveResponseIds[0]
        const { loggedInUserId } = PositiveResponseCrudService

        manageState([
            { key: "busyId", value: TicketPositiveResponseId }
        ])

        axios.get(`${process.env.REACT_APP_API_URL}/api/positiveResponse/IgnorePositiveResponseErrors/${TicketPositiveResponseId}/${loggedInUserId}`, AxiosConfig.configValue())
            .then(() => {
                // snackBar({ text: "Ignored positive response", type: "secondary", timeout: 4000 })
                // Recursively call the function for the remaining IDs
                PositiveResponseCrudService.ignorePositiveResponseErrors(ticketPositiveResponseIds.slice(1))
            })
            .catch(() => {
                snackBar({ text: "Error ignoring positive response", type: "error", timeout: 4000 })
                manageState([
                    { key: "busyId", value: -1 }
                ])
            })
    }
}

export default PositiveResponseCrudService